// Knowledge Base — Intelligence Briefings (Reports) panel.
//
// Lives at /admin/knowledge-base/reports and renders past briefings + a
// generate/send CTA. Briefings are the Phase-2 monthly executive output
// of the Knowledge Base.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Brain,
  Loader2,
  Mail,
  Wand2,
  Calendar,
  CheckCircle2,
  Eye,
  Sparkles,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminKnowledgeBaseReports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sendOpen, setSendOpen] = useState(null); // report_id | "new"
  const [sendEmail, setSendEmail] = useState("");
  const [sendIncludePdf, setSendIncludePdf] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/kb/reports");
      setReports(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load reports"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const generateNow = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/kb/reports/generate", { save: true });
      const placeholderId = r.data.id;
      toast.info("Claude is researching… polling for completion (~60-90s)");
      // Poll every 4s for up to 3 minutes for status === "ready".
      const deadline = Date.now() + 180_000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((res) => setTimeout(res, 4000));
        try {
          const check = await api.get(`/kb/reports/${placeholderId}`);
          if (check.data.status === "ready") {
            toast.success(`Briefing ready — ${check.data.period}`);
            await load();
            navigate(`/admin/knowledge-base/reports/${placeholderId}`);
            return;
          }
          if (check.data.status === "failed") {
            toast.error(`Generation failed: ${check.data.error || "unknown"}`);
            await load();
            return;
          }
        } catch {
          // transient — keep polling
        }
        if (Date.now() > deadline) {
          toast.warning("Generation is taking longer than 3 minutes — check back later.");
          await load();
          return;
        }
      }
    } catch (e) {
      toast.error(formatApiError(e, "Generation failed"));
    } finally {
      setGenerating(false);
    }
  };

  const send = async (report_id) => {
    setSending(true);
    try {
      const body = {
        to_email: sendEmail.trim() || undefined,
        include_investor_pdf: sendIncludePdf,
      };
      if (report_id !== "new") body.report_id = report_id;
      const r = await api.post("/kb/reports/send", body);
      toast.success(
        `Briefing emailed to ${r.data.to}${r.data.investor_pdf_attached ? " (+ Investor PDF)" : ""}`
      );
      setSendOpen(null);
      setSendEmail("");
      setSendIncludePdf(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Send failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="kb-reports-page"
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 space-y-6"
    >
      <Link
        to="/admin/knowledge-base"
        data-testid="kb-reports-back"
        className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-slate-400 hover:text-slate-200"
      >
        <ArrowLeft className="h-3 w-3" /> Knowledge Base
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-sky-400 flex items-center gap-1.5">
            <Brain className="h-3 w-3" /> Intelligence Briefings
          </div>
          <h1 className="mt-1 text-3xl font-bold text-slate-100">
            Monthly Nodera Intelligence Briefing
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            A 4-section executive briefing across the entire Knowledge Base — trends, vendor
            network health, clinic opportunities, and competitive threats. Auto-runs the 1st Monday
            of every month at 09:00 UTC; generate-on-demand any time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="kb-reports-generate-btn"
            onClick={generateNow}
            disabled={generating}
            className="bg-sky-500 hover:bg-sky-400 text-white"
          >
            {generating ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Claude is researching… ~60s
              </>
            ) : (
              <>
                <Wand2 className="mr-1.5 h-3.5 w-3.5" /> Generate now
              </>
            )}
          </Button>
          <Button
            data-testid="kb-reports-send-new-btn"
            onClick={() => setSendOpen("new")}
            variant="outline"
            disabled={generating}
            className="bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70"
          >
            <Mail className="mr-1.5 h-3.5 w-3.5" /> Generate &amp; email
          </Button>
        </div>
      </header>

      {/* Reports list */}
      <section data-testid="kb-reports-list">
        {loading ? (
          <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-10 text-center text-sm text-slate-400">
            Loading…
          </div>
        ) : reports.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-slate-950/40 p-10 text-center">
            <Brain className="mx-auto h-7 w-7 text-slate-500" />
            <p className="mt-2 text-sm text-slate-300">
              No briefings yet. Click "Generate now" to create your first one.
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              Claude reads the entire Knowledge Base and produces a 4-section briefing in ~60s.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {reports.map((r) => {
              const trends_n = (r.trends?.items || []).length;
              const vendors_n = (r.vendor_network?.items || []).length;
              const clinics_n = (r.clinic_opportunities?.items || []).length;
              const threats_n = (r.competitive_threats?.items || []).length;
              return (
                <li
                  key={r.id}
                  data-testid={`kb-report-${r.id}`}
                  className="group rounded-2xl border border-white/10 bg-slate-950/60 p-5 hover:border-sky-500/40 transition"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <Link
                      to={`/admin/knowledge-base/reports/${r.id}`}
                      className="min-w-0 flex-1"
                    >
                      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] font-semibold text-sky-400">
                        <Calendar className="h-3 w-3" /> {r.period}
                      </div>
                      <h3 className="mt-1 text-lg font-semibold text-slate-100 group-hover:text-white">
                        Nodera Intelligence Briefing — {r.period}
                      </h3>
                      <p className="mt-1 text-xs text-slate-500">
                        Generated {new Date(r.generated_at).toLocaleString()} · model{" "}
                        {r.model || "claude-sonnet-4-5"}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span className="rounded-full border border-white/10 bg-slate-900/60 px-2 py-0.5 text-slate-200">
                          {trends_n} trends
                        </span>
                        <span className="rounded-full border border-white/10 bg-slate-900/60 px-2 py-0.5 text-slate-200">
                          {vendors_n} vendor signals
                        </span>
                        <span className="rounded-full border border-white/10 bg-slate-900/60 px-2 py-0.5 text-slate-200">
                          {clinics_n} clinic opps
                        </span>
                        <span className="rounded-full border border-white/10 bg-slate-900/60 px-2 py-0.5 text-slate-200">
                          {threats_n} threats
                        </span>
                        {r.delivered_to && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 px-2 py-0.5 font-semibold">
                            <CheckCircle2 className="h-3 w-3" />
                            Emailed to {r.delivered_to}
                          </span>
                        )}
                      </div>
                    </Link>
                    <div className="flex items-center gap-2 shrink-0">
                      <Link
                        to={`/admin/knowledge-base/reports/${r.id}`}
                        data-testid={`kb-report-open-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-slate-900/60 px-2.5 py-1.5 text-xs text-slate-200 hover:bg-slate-900/90"
                      >
                        <Eye className="h-3 w-3" /> Open
                      </Link>
                      <button
                        data-testid={`kb-report-send-${r.id}`}
                        onClick={() => setSendOpen(r.id)}
                        className="inline-flex items-center gap-1 rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-1.5 text-xs text-sky-200 hover:bg-sky-500/20"
                      >
                        <Mail className="h-3 w-3" /> Email
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Send modal */}
      {sendOpen && (
        <div
          data-testid="kb-reports-send-modal"
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !sending && setSendOpen(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-sky-500/30 bg-slate-950 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-sky-300">
              <Mail className="h-4 w-4" />
              <span className="text-[11px] uppercase tracking-[0.16em] font-semibold">
                Email briefing via Resend
              </span>
            </div>
            <h3 className="mt-1 text-xl font-bold text-slate-100">
              {sendOpen === "new" ? "Generate & email new briefing" : "Email this briefing"}
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              Leave blank to send to the admin's default delivery address (your digest delivery
              email if set, otherwise your account email).
            </p>
            <Input
              data-testid="kb-reports-send-input"
              type="email"
              autoFocus
              value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              placeholder="recipient@noderahealth.com (optional)"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !sending) send(sendOpen);
              }}
              className="mt-3 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-500"
            />
            <label
              data-testid="kb-reports-send-include-pdf"
              className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-slate-200"
            >
              <input
                type="checkbox"
                checked={sendIncludePdf}
                onChange={(e) => setSendIncludePdf(e.target.checked)}
                className="mt-0.5 accent-amber-500"
                disabled={sending}
              />
              <span>
                <span className="font-semibold text-amber-200">
                  Also attach Investor PDF
                </span>
                <span className="block mt-0.5 text-[11px] text-slate-400">
                  Claude rewrites the briefing with vendor/clinic/competitor names abstracted and
                  pricing tiered, then attaches the rendered PDF. Adds ~45-60s if not previously cached.
                </span>
              </span>
            </label>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                disabled={sending}
                onClick={() => setSendOpen(null)}
                className="bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70"
              >
                Cancel
              </Button>
              <Button
                data-testid="kb-reports-send-confirm"
                onClick={() => send(sendOpen)}
                disabled={sending}
              >
                {sending ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    {sendOpen === "new" ? "Generating + sending…" : "Sending…"}
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Send
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
